import { useCallback, useEffect, useRef, useState } from 'react';

type DelayedSetState<T> = (
  newValue: T | ((prev: T) => T),
  delay?: number
) => void;

export function useDelayedState<T>(initialValue: T): [T, DelayedSetState<T>] {
  const [state, setState] = useState<T>(initialValue);
  const timeoutRef = useRef<number | null>(null);

  const clearPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setStateWithDelay: DelayedSetState<T> = useCallback(
    (setStateAction, delay) => {
      clearPending();

      if (!delay) {
        setState(setStateAction);
        return;
      }

      timeoutRef.current = setTimeout(() => {
        setState(setStateAction);
        timeoutRef.current = null;
      }, delay);
    },
    [clearPending]
  );

  useEffect(() => () => clearPending(), [clearPending]);

  return [state, setStateWithDelay];
}
